import { ReactNode } from "react";

export type LegalType = "imprint" | "privacy" | "terms";

interface LegalSection {
    title: string;
    description?: string;
    content: ReactNode;
}

const legalSections: Record<LegalType, LegalSection> = {
    imprint: {
        title: "Impressum",
        content: (
            <div className="space-y-4 text-sm">
                <div className="space-y-1">
                    <p className="font-semibold">Johannes Uray</p>
                    <p>Kloiberweg 5c/4</p>
                    <p>8041 Graz</p>
                    <p>Österreich</p>
                    <p>E-Mail: zaehler-portal@plant-control.net</p>
                </div>
                <div className="text-muted-foreground text-xs pt-4 border-t">
                    Informationspflicht laut §5 E-Commerce Gesetz, §14 Unternehmensgesetzbuch, §63 Gewerbeordnung und Offenlegungspflicht laut §25 Mediengesetz.
                </div>
            </div>
        ),
    },
    privacy: {
        title: "Datenschutzerklärung",
        description: "Informationen zur Verarbeitung Ihrer personenbezogenen Daten.",
        content: (
            <div className="space-y-4 text-sm">
                <section className="space-y-2">
                    <h3 className="font-semibold">1. Verantwortlicher</h3>
                    <p>
                        Johannes Uray<br />
                        Kloiberweg 5c/4, 8041 Graz, Österreich<br />
                        E-Mail: zaehler-portal@plant-control.net
                    </p>
                </section>

                <section className="space-y-2">
                    <h3 className="font-semibold">2. Gespeicherte Daten und Rechtsgrundlagen</h3>
                    <p>
                        Wir speichern und verarbeiten folgende personenbezogene Daten:
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Zugangsdaten (Benutzername, E-Mail-Adresse) — Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</li>
                        <li>Zählerdaten (Zählernummern, Verbrauchsdaten, Messwerte) — Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</li>
                        <li>Anlagendaten (Adressen, Zuordnungen) — Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</li>
                        <li>Zugriffsstatistiken (IP-Adresse, User-Agent, Zugriffszeiten, genutzte Bereiche) — Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an Betriebssicherheit und Optimierung)</li>
                    </ul>
                </section>

                <section className="space-y-2">
                    <h3 className="font-semibold">3. Zweck und Ort der Verarbeitung</h3>
                    <p>
                        Die Datenverarbeitung erfolgt zum Zweck der Bereitstellung, Wartung und Verbesserung unseres Zählerdaten-Portals sowie zur Sicherstellung der Betriebssicherheit.
                    </p>
                    <p>
                        Die Daten werden auf Servern der <strong>netcup GmbH</strong> in <strong>Deutschland</strong> gehostet. Ein Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO wurde mit netcup abgeschlossen.
                    </p>
                </section>

                <section className="space-y-2">
                    <h3 className="font-semibold">4. Aufbewahrung und Löschung</h3>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Zugriffsstatistiken: standardmäßig 30 Tage (konfigurierbar in den System-Einstellungen)</li>
                        <li>Zugangsdaten: bis zur Löschung Ihres Kontos</li>
                        <li>Zähler- und Anlagendaten: für die Dauer der Vertragsbeziehung bzw. gemäß gesetzlicher Aufbewahrungsfristen</li>
                    </ul>
                </section>

                <section className="space-y-2">
                    <h3 className="font-semibold">5. Cookies & Local Storage</h3>
                    <p>
                        Wir verwenden notwendige Cookies und Local Storage, um Ihre Sitzung zu verwalten und Ihre Einstellungen (z.B. Dark Mode, Tabellenansicht) zu speichern. Es werden keine Tracking-Cookies von Drittanbietern verwendet.
                    </p>
                </section>

                <section className="space-y-2">
                    <h3 className="font-semibold">6. Ihre Rechte</h3>
                    <p>
                        Ihnen stehen die Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit, Widerruf und Widerspruch zu. Zur Ausübung Ihrer Rechte wenden Sie sich bitte an: zaehler-portal@plant-control.net
                    </p>
                    <p>
                        Wenn Sie der Auffassung sind, dass die Verarbeitung Ihrer Daten gegen das Datenschutzrecht verstößt, können Sie sich bei der zuständigen Aufsichtsbehörde beschweren:
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Österreich: Österreichische Datenschutzbehörde (dsb.gv.at)</li>
                        <li>Deutschland: Die für Ihren Wohnsitz zuständige Landesdatenschutzbehörde</li>
                    </ul>
                </section>

                <section className="space-y-2">
                    <h3 className="font-semibold">7. Auftragsverarbeiter</h3>
                    <p>
                        Wir setzen folgenden Auftragsverarbeiter gemäß Art. 28 DSGVO ein:
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>netcup GmbH</strong> — Hosting der Anwendung und Datenbanken (Serverstandort: Deutschland). Ein AVV wurde abgeschlossen.</li>
                    </ul>
                </section>
            </div>
        ),
    },
    terms: {
        title: "Nutzungsbedingungen",
        description: "Allgemeine Bedingungen für die Nutzung des Zähler-Portal.",
        content: (
            <div className="space-y-4 text-sm">
                <section className="space-y-2">
                    <h3 className="font-semibold">1. Geltungsbereich</h3>
                    <p>
                        Diese Nutzungsbedingungen gelten für die Nutzung des Zählerdaten-Portals. Mit der Registrierung und Nutzung stimmen Sie diesen Bedingungen zu.
                    </p>
                </section>
                <section className="space-y-2">
                    <h3 className="font-semibold">2. Leistungsbeschreibung</h3>
                    <p>
                        Das Portal ermöglicht die Einsichtnahme in Verbrauchsdaten Ihrer zugeordneten Zähler (Strom, Wasser, Wärme, etc.). Die Daten werden periodisch aktualisiert.
                    </p>
                </section>
                <section className="space-y-2">
                    <h3 className="font-semibold">3. Pflichten des Nutzers</h3>
                    <p>
                        Sie verpflichten sich, Ihre Zugangsdaten geheim zu halten und vor dem Zugriff Dritter zu schützen. Bei Verdacht auf Missbrauch ist der Betreiber umgehend zu informieren.
                    </p>
                </section>
                <section className="space-y-2">
                    <h3 className="font-semibold">4. Haftung</h3>
                    <p>
                        Der Betreiber bemüht sich um hohe Verfügbarkeit und Datengenauigkeit, übernimmt jedoch keine Gewähr für die ständige Erreichbarkeit des Portals oder die absolute Fehlerfreiheit der übermittelten Zählerdaten.
                    </p>
                </section>
                <section className="space-y-2">
                    <h3 className="font-semibold">5. Änderungen</h3>
                    <p>
                        Wir behalten uns das Recht vor, diese Nutzungsbedingungen jederzeit zu ändern. Änderungen werden Ihnen im Portal bekannt gegeben.
                    </p>
                </section>
                <section className="space-y-2">
                    <h3 className="font-semibold">6. Kündigung und Kontolöschung</h3>
                    <p>
                        Sie können Ihr Nutzerkonto jederzeit per E-Mail an zaehler-portal@plant-control.net kündigen und die Löschung Ihrer personenbezogenen Daten beantragen. Die Löschung erfolgt unter Berücksichtigung etwaiger gesetzlicher Aufbewahrungsfristen.
                    </p>
                </section>
                <section className="space-y-2">
                    <h3 className="font-semibold">7. Anwendbares Recht und Gerichtsstand</h3>
                    <p>
                        Es gilt österreichisches Recht. Gerichtsstand ist Graz, Österreich. Zwingende gesetzliche Bestimmungen zum Schutz von Verbrauchern am jeweiligen Wohnsitz bleiben unberührt.
                    </p>
                </section>
            </div>
        ),
    },
};

export function getLegalSection(type: LegalType): LegalSection {
    return legalSections[type];
}
